/** Peças pequenas: Alert, Skeleton, Spinner, EmptyState, PageHeader, KeyValue. */
import { cva, type VariantProps } from "class-variance-authority";
import { AlertCircle, CheckCircle2, Info, Loader2, TriangleAlert } from "lucide-react";
import type { HTMLAttributes, ReactNode } from "react";
import { t } from "@/i18n";
import { cn } from "@/lib/utils";

const alertVariants = cva("flex gap-3 rounded-lg border p-3 text-sm [&>svg]:mt-0.5 [&>svg]:size-4 [&>svg]:shrink-0", {
  variants: {
    tone: {
      info: "border-info/30 bg-info/5 [&>svg]:text-info",
      success: "border-success/30 bg-success/5 [&>svg]:text-success",
      warning: "border-warning/50 bg-warning/10 [&>svg]:text-warning",
      destructive: "border-destructive/30 bg-destructive/5 [&>svg]:text-destructive",
    },
  },
  defaultVariants: { tone: "info" },
});

const ALERT_ICONS = { info: Info, success: CheckCircle2, warning: TriangleAlert, destructive: AlertCircle };

export function Alert({
  tone = "info",
  title,
  children,
  action,
  className,
  ...props
}: Omit<HTMLAttributes<HTMLDivElement>, "title"> & VariantProps<typeof alertVariants> & { title?: ReactNode; action?: ReactNode }) {
  const Icon = ALERT_ICONS[tone ?? "info"];
  return (
    <div role={tone === "destructive" ? "alert" : "status"} className={cn(alertVariants({ tone }), className)} {...props}>
      <Icon aria-hidden />
      <div className="grid flex-1 gap-1">
        {title && <p className="font-medium leading-tight">{title}</p>}
        {children && <div className="text-muted-foreground [&_a]:underline">{children}</div>}
      </div>
      {action && <div className="shrink-0 self-center">{action}</div>}
    </div>
  );
}

export function Skeleton({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn("animate-pulse rounded-md bg-muted", className)} {...props} />;
}

export function Spinner({ className, label }: { className?: string; label?: string }) {
  return (
    <span role="status" className={cn("inline-flex items-center gap-2 text-sm text-muted-foreground", className)}>
      <Loader2 className="size-4 animate-spin" aria-hidden />
      {label ?? <span className="sr-only">{t("common.loading")}</span>}
    </span>
  );
}

export function EmptyState({
  icon,
  title,
  description,
  action,
  className,
}: {
  icon?: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("flex flex-col items-center justify-center gap-3 rounded-xl border border-dashed p-10 text-center", className)}>
      {icon && <div className="rounded-full bg-muted p-3 text-muted-foreground [&_svg]:size-6">{icon}</div>}
      <div className="grid gap-1">
        <p className="font-medium">{title}</p>
        {description && <p className="max-w-md text-sm text-muted-foreground">{description}</p>}
      </div>
      {action}
    </div>
  );
}

export function PageHeader({
  title,
  description,
  actions,
  breadcrumb,
}: {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  breadcrumb?: ReactNode;
}) {
  return (
    <div className="mb-6 grid gap-2">
      {breadcrumb}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="grid min-w-0 gap-1">
          <h1 className="truncate text-2xl font-semibold tracking-tight">{title}</h1>
          {description && <p className="text-sm text-muted-foreground">{description}</p>}
        </div>
        {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
      </div>
    </div>
  );
}

/** Linha rótulo/valor em listas de detalhes. */
export function KeyValue({ label, children }: { label: ReactNode; children: ReactNode }) {
  return (
    <div className="grid gap-0.5 sm:grid-cols-[10rem_1fr] sm:gap-3">
      <dt className="text-sm text-muted-foreground">{label}</dt>
      <dd className="min-w-0 break-all text-sm">{children}</dd>
    </div>
  );
}
